/**
 * Saving Content Hub files to the computer.
 *
 * A file is fetched and handed to the browser as a download. Anything that cannot be
 * fetched (a host without CORS, e.g. scraped Instagram/TikTok media, a 404, a network error) is reported
 * as failed: we never point the Omni tab itself at the file, because a cross-origin URL ignores the
 * `download` attribute and would replace Omni with the file, stopping the rest of a bulk download.
 */

export function getUrlHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

/** Fetch the file and save it under `filename`; false when the fetch fails (CORS, 404, network). */
async function fetchAndSave(url: string, filename: string, token?: string | null): Promise<boolean> {
  try {
    const headers: Record<string, string> = {};
    const apiHost = getUrlHost(import.meta.env.VITE_BEATLY_API_URL || '');
    if (token && apiHost && getUrlHost(url) === apiHost) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Free the blob a moment after the click. Guarded: the timer can outlive a test's URL mock (Omni pipeline 268).
    window.setTimeout(() => {
      try { window.URL.revokeObjectURL?.(objectUrl); } catch { /* nothing to free */ }
    }, 1000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Save one Content Hub file. Resolves `true` when the file was handed to the browser as a download,
 * `false` when it could not be (the caller tells the user and offers to open it in a new tab).
 * It never navigates the current tab.
 */
export async function downloadContentHubFile(url: string, filename: string, token?: string | null): Promise<boolean> {
  return fetchAndSave(url, filename, token);
}

/**
 * The one message shown after a download in which some files could not be saved.
 *
 * - `failed` / `total`: files that could not be saved, out of the files asked for.
 * - `keptSelected`: the failed files stay selected in the gallery (bulk download).
 * - `webFallbacks`: other files saved as their web version because no original is stored
 *   (folded in here so the user gets one message, not two).
 * - `canOpenInNewTab`: the single failed file has an address, so the message has an "Open in new tab" button.
 */
export function getContentHubDownloadFailedMessage({ failed, total, keptSelected, webFallbacks = 0, canOpenInNewTab = true }: {
  failed: number;
  total: number;
  keptSelected: boolean;
  webFallbacks?: number;
  canOpenInNewTab?: boolean;
}): string {
  const parts = [
    total === 1 ? "The file couldn't be downloaded." : `${failed} of ${total} files couldn't be downloaded.`,
  ];
  if (keptSelected) parts.push(failed === 1 ? "It's still selected." : "They're still selected.");
  if (failed > 1) parts.push('Open each one, click Download, then Open in new tab to save it from there.');
  else if (canOpenInNewTab) parts.push('Click Open in new tab and save it from there.');
  if (webFallbacks > 0) {
    parts.push(
      webFallbacks === 1
        ? 'One other file has no stored original, so its web version was downloaded.'
        : `${webFallbacks} other files have no stored original, so their web version was downloaded.`,
    );
  }
  return parts.join(' ');
}
